import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'
import { get } from 'lodash'

import ErrorMessage from './ErrorMessage'
import { useFormContext } from '@context/FormContext'

/**
 * Input component - Text input field integrated with form context
 * Supports validation, error display, and various input types
 * 
 * @param {string} name - Field name (supports nested paths like 'contactInfo.email')
 * @param {string} label - Label text for the input
 * @param {string} autocomplete - HTML autocomplete attribute
 * @param {string} type - Input type (text, email, tel, etc.)
 * @param {Object} props - Additional props passed to the input element
 */
const Input = ({ name, label, autocomplete = 'off', type = 'text', ...props }) => {
  const { values, errors, submitCount, setFieldValue, setFieldError } = useFormContext()
  
  // Get current field value from form context (supports nested paths)
  const fieldValue = get(values, name) || ''
  const fieldError = get(errors, name)
  
  // Only show errors after user tries to submit (submitCount > 0)
  const fieldTouched = submitCount > 0

  /**
   * Handle input value changes
   * Updates form context and clears error if one exists
   */
  const handleChange = (e) => {
    const newValue = e.target.value
    setFieldValue(name, newValue)
    
    // Clear error when user starts typing (if error exists)
    if (fieldError) {
      setFieldError(name, undefined)
    }
    
    // Call custom onChange handler if provided
    props.onChange && props.onChange(newValue)
  }

  return (
    <div
      className={classNames(
        'mb-4',
        { 'opacity-60': props.disabled }
      )}
    >
      {label && (
        <label className="form-label" htmlFor={name}>
          {label}
        </label>
      )}

      <input
        {...props}
        id={name}
        name={name}
        className={classNames(
          'form-input',
          props.className
        )}
        type={type}
        value={fieldValue}
        onChange={handleChange}
        onKeyDown={type === 'email' ? (e) => {
          // Allow: backspace, delete, tab, escape, enter, and special keys
          if ([8, 9, 27, 13, 46].indexOf(e.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true) ||
            // Allow: home, end, left, right
            (e.keyCode >= 35 && e.keyCode <= 39)) {
            return
          }
          // Allow: letters, numbers, @, ., -, _, +
          // KeyCode 190 = period (.), 46 = DELETE (already handled above)
          if ((e.keyCode >= 48 && e.keyCode <= 57) ||
            (e.keyCode >= 65 && e.keyCode <= 90) ||
            (e.keyCode >= 96 && e.keyCode <= 105) ||
            [64, 190, 45, 95, 43].indexOf(e.keyCode) !== -1) {
            return
          }
          e.preventDefault()
        } : undefined}
      />

      {props.suggestions && (
        <datalist id={`${name}_datalist`}>
          {props.suggestions.map((suggestion) => (
            <option key={suggestion.value} value={suggestion.value}>
              {suggestion.label}
            </option>
          ))}
        </datalist>
      )}

      <ErrorMessage render={!!(fieldError && fieldTouched)} message={fieldError} />
    </div>
  )
}

Input.propTypes = {
  name: PropTypes.string.isRequired,
  autocomplete: PropTypes.string,
  autofocus: PropTypes.bool,
  disabled: PropTypes.bool,
  keyboard: PropTypes.oneOf(['decimal', 'email', 'none', 'numeric', 'search', 'tel', 'text', 'url']),
  label: PropTypes.string,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  step: PropTypes.number,
  type: PropTypes.oneOf(['email', 'number', 'password', 'search', 'tel', 'text', 'url']),
  suggestions: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    value: PropTypes.any.isRequired,
  })),
  className: PropTypes.string,
}

export default Input
